/**
 * Research (ahoy_research) CRM trigger card variant — Header, collapsed
 * PreviewBody, and expanded Body.
 */

import { AlertCircle, CheckCircle, Clock, Loading01 } from '@untitledui/icons';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';

import { getStringValue } from './helpers';

/**
 * Helper that pulls the research fields out of a trigger so the Header,
 * Preview and Body components share extraction logic. Mirrors the
 * frontend-side `extractResearchData` (lib/crm/record/research/useResearch),
 * but operates on the flat decrypted event payload returned by
 * `hydrate_trigger_events` instead of a full CrmObject.
 */
type ResearchStatus = 'queued' | 'in_progress' | 'complete' | 'failed';

const RESEARCH_STATUS_VALUES: readonly ResearchStatus[] = [
  'queued',
  'in_progress',
  'complete',
  'failed',
] as const;

function isResearchStatus(value: unknown): value is ResearchStatus {
  return (
    typeof value === 'string' &&
    (RESEARCH_STATUS_VALUES as readonly string[]).includes(value)
  );
}

interface ResearchData {
  status: ResearchStatus;
  summary: string | null;
  errorMessage: string | null;
  completedAt: string | null;
  createdAt: string | null;
}

function getResearchData(data: CrmEventTriggerData): ResearchData {
  const { event } = data.crm_event;
  const rawStatus = getStringValue(event, 'ahoy_status');
  return {
    status: isResearchStatus(rawStatus) ? rawStatus : 'queued',
    summary: getStringValue(event, 'ahoy_summary'),
    errorMessage: getStringValue(event, 'ahoy_error_message'),
    completedAt: getStringValue(event, 'ahoy_completed_at'),
    createdAt: getStringValue(event, 'ahoy_created_at'),
  };
}

interface ResearchStatusMeta {
  label: string;
  Icon: typeof CheckCircle;
  iconClass: string;
}

const RESEARCH_STATUS_META: Record<ResearchStatus, ResearchStatusMeta> = {
  queued: {
    label: 'Queued',
    Icon: Clock,
    iconClass: 'text-fg-tertiary',
  },
  in_progress: {
    label: 'In progress',
    Icon: Loading01,
    iconClass: 'text-utility-brand-500 animate-spin',
  },
  complete: {
    label: 'Complete',
    Icon: CheckCircle,
    iconClass: 'text-utility-success-500',
  },
  failed: {
    label: 'Failed',
    Icon: AlertCircle,
    iconClass: 'text-utility-error-500',
  },
};

export function ResearchHeader({ data }: { data: CrmEventTriggerData }) {
  const { status, completedAt, createdAt } = getResearchData(data);
  const meta = RESEARCH_STATUS_META[status];
  const timestamp = completedAt || createdAt;
  const relativeTime = timestamp ? formatRelativeTime(timestamp) : null;
  return (
    <div className='flex-1 min-w-0'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-primary flex-shrink-0'>
          Research
        </span>
        <span className='text-tertiary flex-shrink-0'>·</span>
        <span className='text-xs text-tertiary flex-shrink-0'>
          {meta.label}
        </span>
        {relativeTime && (
          <>
            <span className='text-tertiary flex-shrink-0'>·</span>
            <span className='text-xs text-tertiary flex-shrink-0'>
              {relativeTime}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** Compact research preview — line-clamped summary (or error message). */
export function ResearchPreviewBody({
  data,
  orgId,
}: {
  data: CrmEventTriggerData;
  orgId?: number;
}) {
  const { status, summary, errorMessage } = getResearchData(data);
  if (status === 'failed' && errorMessage) {
    return (
      <div className='text-xs text-utility-error-700 line-clamp-2'>
        {errorMessage}
      </div>
    );
  }
  if (summary) {
    return (
      <div className='text-xs text-tertiary line-clamp-2 overflow-hidden'>
        <MarkdownContent content={summary} size='compact' orgId={orgId} />
      </div>
    );
  }
  if (status === 'in_progress') {
    return (
      <div className='text-xs text-tertiary italic'>Research in progress…</div>
    );
  }
  if (status === 'queued') {
    return (
      <div className='text-xs text-tertiary italic'>Waiting to start…</div>
    );
  }
  return null;
}

export function ResearchBody({
  data,
  orgId,
}: {
  data: CrmEventTriggerData;
  orgId?: number;
}) {
  const { status, summary, errorMessage, completedAt, createdAt } =
    getResearchData(data);
  const meta = RESEARCH_STATUS_META[status];
  const StatusIcon = meta.Icon;
  const timestamp = completedAt || createdAt;
  const relativeTime = timestamp ? formatRelativeTime(timestamp) : null;
  return (
    <div className='space-y-3'>
      <div>
        <div className='text-xs font-medium text-secondary mb-1'>Status</div>
        <div className='text-xs text-secondary flex items-center gap-1.5'>
          <StatusIcon
            className={`w-3.5 h-3.5 flex-shrink-0 ${meta.iconClass}`}
            strokeWidth={2}
          />
          <span>{meta.label}</span>
          {relativeTime && (
            <>
              <span className='text-tertiary'>·</span>
              <span>{relativeTime}</span>
            </>
          )}
        </div>
      </div>

      {status === 'failed' && errorMessage && (
        <div>
          <div className='text-xs font-medium text-secondary mb-1'>Error</div>
          <div className='text-xs text-utility-error-700 whitespace-pre-wrap leading-relaxed'>
            {errorMessage}
          </div>
        </div>
      )}

      {summary && (
        <div>
          <div className='text-xs font-medium text-secondary mb-1'>Summary</div>
          <MarkdownContent
            content={summary}
            className='text-xs text-secondary leading-relaxed'
            size='compact'
            orgId={orgId}
          />
        </div>
      )}
    </div>
  );
}
